import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    instructions: '',
    isRequired: false,
    estimatedTime: '',
    tags: [],
    fields: []
  });
  
  const [activeMode, setActiveMode] = useState('build'); // 'build' or 'preview'
  const [showFieldTypes, setShowFieldTypes] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [draggedField, setDraggedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const fieldTypes = [
    {
      type: 'text',
      label: 'Text Input',
      icon: '📝',
      description: 'Single line text input'
    },
    {
      type: 'textarea',
      label: 'Text Area',
      icon: '📄',
      description: 'Multi-line text input'
    },
    {
      type: 'email',
      label: 'Email',
      icon: '📧',
      description: 'Email address input'
    },
    {
      type: 'tel',
      label: 'Phone',
      icon: '📞',
      description: 'Phone number input'
    },
    {
      type: 'number',
      label: 'Number',
      icon: '🔢',
      description: 'Numeric input'
    },
    {
      type: 'date',
      label: 'Date',
      icon: '📅',
      description: 'Date picker'
    },
    {
      type: 'select',
      label: 'Dropdown',
      icon: '📋',
      description: 'Select from options'
    },
    {
      type: 'radio',
      label: 'Radio Buttons',
      icon: '🔘',
      description: 'Single choice from options'
    },
    {
      type: 'checkbox',
      label: 'Checkbox',
      icon: '☑️',
      description: 'Multiple selections'
    },
    {
      type: 'file',
      label: 'File Upload',
      icon: '📎',
      description: 'File attachment'
    }
  ];

  const categories = [
    'Patient Forms',
    'HR Forms',
    'Administrative',
    'Financial',
    'Research',
    'Other'
  ];

  useEffect(() => {
    if (isEditing) {
      // Simulate loading existing form data
      const loadForm = () => {
        const mockForm = {
          title: 'Patient Registration Form',
          description: 'Comprehensive patient registration form for new admissions',
          category: 'Patient Forms',
          instructions: 'Please fill out all required fields marked with an asterisk (*)',
          isRequired: true,
          estimatedTime: '10',
          tags: ['Registration', 'Patient'],
          fields: [
            {
              id: '1',
              type: 'text',
              label: 'Full Name',
              placeholder: 'Enter your full name',
              required: true,
              helpText: ''
            },
            {
              id: '2',
              type: 'email',
              label: 'Email Address',
              placeholder: 'Enter your email address',
              required: true,
              helpText: 'We will use this for communication'
            }
          ]
        };
        setForm(mockForm);
      };
      
      setTimeout(loadForm, 500);
    }
  }, [isEditing]);

  const addField = (fieldType) => {
    const newField = {
      id: Date.now().toString(),
      type: fieldType.type,
      label: `New ${fieldType.label}`,
      placeholder: '',
      required: false,
      helpText: '',
      options: ['select', 'radio', 'checkbox'].includes(fieldType.type) ? ['Option 1', 'Option 2'] : undefined
    };
    
    setForm(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    
    setSelectedField(newField.id);
    setShowFieldTypes(false);
    setHasUnsavedChanges(true);
  };

  const updateField = (fieldId, updates) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.map(field =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
    setHasUnsavedChanges(true);
  };

  const deleteField = (fieldId) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
    setSelectedField(null);
    setHasUnsavedChanges(true);
  };

  const duplicateField = (fieldId) => {
    const fieldToDuplicate = form.fields.find(field => field.id === fieldId);
    if (fieldToDuplicate) {
      const duplicatedField = {
        ...fieldToDuplicate,
        id: Date.now().toString(),
        label: `${fieldToDuplicate.label} (Copy)`
      };
      
      const fieldIndex = form.fields.findIndex(field => field.id === fieldId);
      const newFields = [...form.fields];
      newFields.splice(fieldIndex + 1, 0, duplicatedField);
      
      setForm(prev => ({ ...prev, fields: newFields }));
      setSelectedField(duplicatedField.id);
      setHasUnsavedChanges(true);
    }
  };

  const moveField = (fieldId, direction) => {
    const fieldIndex = form.fields.findIndex(field => field.id === fieldId);
    if (
      (direction === 'up' && fieldIndex > 0) ||
      (direction === 'down' && fieldIndex < form.fields.length - 1)
    ) {
      const newFields = [...form.fields];
      const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
      [newFields[fieldIndex], newFields[newIndex]] = [newFields[newIndex], newFields[fieldIndex]];
      
      setForm(prev => ({ ...prev, fields: newFields }));
      setHasUnsavedChanges(true);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setHasUnsavedChanges(false);
      navigate('/forms');
    } catch (error) {
      console.error('Error saving form:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFieldPreview = (field) => {
    const commonProps = {
      className: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
      placeholder: field.placeholder,
      disabled: true
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return <input type={field.type} {...commonProps} />;
      case 'number':
        return <input type="number" {...commonProps} />;
      case 'date':
        return <input type="date" {...commonProps} />;
      case 'textarea':
        return <textarea {...commonProps} rows={3} />;
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <label key={index} className="flex items-center">
                <input type="radio" name={field.id} className="mr-2" disabled />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <label key={index} className="flex items-center">
                <input type="checkbox" className="mr-2" disabled />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'file':
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
            <span className="text-gray-500">Click to upload or drag and drop</span>
          </div>
        );
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/forms')}
                  className="mr-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Forms
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Form' : 'Create New Form'}
                </h1>
                {hasUnsavedChanges && (
                  <span className="ml-2 text-sm text-orange-600">• Unsaved changes</span>
                )}
              </div>

              <div className="flex items-center space-x-3">
                {/* Mode Toggle */}
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setActiveMode('build')}
                    className={`${
                      activeMode === 'build'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <Cog6ToothIcon className="h-4 w-4 mr-2" />
                    Build
                  </button>
                  <button
                    onClick={() => setActiveMode('preview')}
                    className={`${
                      activeMode === 'preview'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-4 py-2 -ml-px rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Preview
                  </button>
                </div>

                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckIcon className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Saving...' : 'Save Form'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Settings Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Form Settings</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Form Title *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, title: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter form title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, description: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the purpose of this form"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, category: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={form.instructions}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, instructions: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Instructions for form users"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={form.estimatedTime}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, estimatedTime: e.target.value }));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="10"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.isRequired}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, isRequired: e.target.checked }));
                      setHasUnsavedChanges(true);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Required form
                  </label>
                </div>

                {/* Field Types */}
                {activeMode === 'build' && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => setShowFieldTypes(!showFieldTypes)}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Field
                    </button>
                    
                    {showFieldTypes && (
                      <div className="mt-2 max-h-64 overflow-y-auto">
                        <div className="grid grid-cols-1 gap-2">
                          {fieldTypes.map((fieldType) => (
                            <button
                              key={fieldType.type}
                              onClick={() => addField(fieldType)}
                              className="flex items-center p-3 text-left border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50"
                            >
                              <span className="text-lg mr-3">{fieldType.icon}</span>
                              <div>
                                <div className="font-medium text-sm text-gray-900">{fieldType.label}</div>
                                <div className="text-xs text-gray-500">{fieldType.description}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {activeMode === 'build' ? (
              <div className="space-y-6">
                {/* Form Builder */}
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">Form Fields</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {form.fields.length} field{form.fields.length !== 1 ? 's' : ''} added
                    </p>
                  </div>
                  
                  <div className="p-6">
                    {form.fields.length === 0 ? (
                      <div className="text-center py-12">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No fields added</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Click "Add Field" to start building your form.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {form.fields.map((field, index) => (
                          <div
                            key={field.id}
                            className={`border rounded-lg p-4 ${
                              selectedField === field.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center">
                                <span className="text-lg mr-2">
                                  {fieldTypes.find(ft => ft.type === field.type)?.icon}
                                </span>
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) => updateField(field.id, { label: e.target.value })}
                                  className="font-medium text-gray-900 bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                                />
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </div>
                              
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => moveField(field.id, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                >
                                  <ArrowUpIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => moveField(field.id, 'down')}
                                  disabled={index === form.fields.length - 1}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                >
                                  <ArrowDownIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => duplicateField(field.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <DocumentDuplicateIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setSelectedField(selectedField === field.id ? null : field.id)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <Cog6ToothIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => deleteField(field.id)}
                                  className="p-1 text-red-400 hover:text-red-600"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                            {selectedField === field.id && (
                              <div className="mt-4 p-4 bg-white rounded border space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Field Label
                                  </label>
                                  <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) => updateField(field.id, { label: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                  />
                                </div>
                                
                                {['text', 'textarea', 'email', 'tel', 'number'].includes(field.type) && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Placeholder
                                    </label>
                                    <input
                                      type="text"
                                      value={field.placeholder}
                                      onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                )}
                                
                                {['select', 'radio', 'checkbox'].includes(field.type) && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Options (one per line)
                                    </label>
                                    <textarea
                                      value={field.options?.join('\n') || ''}
                                      onChange={(e) => updateField(field.id, { 
                                        options: e.target.value.split('\n').filter(opt => opt.trim()) 
                                      })}
                                      rows={3}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  </div>
                                )}
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Help Text
                                  </label>
                                  <input
                                    type="text"
                                    value={field.helpText}
                                    onChange={(e) => updateField(field.id, { helpText: e.target.value })}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Optional help text"
                                  />
                                </div>
                                
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    onChange={(e) => updateField(field.id, { required: e.target.checked })}
                                    className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <label className="ml-2 block text-xs text-gray-900">
                                    Required field
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Form Preview */
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">{form.title || 'Untitled Form'}</h2>
                  {form.description && (
                    <p className="text-gray-600 mt-2">{form.description}</p>
                  )}
                  {form.instructions && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                        <p className="text-sm text-blue-700">{form.instructions}</p>
                      </div>
                    </div>
                  )}
                  {form.estimatedTime && (
                    <div className="mt-3 flex items-center text-sm text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      Estimated time: {form.estimatedTime} minutes
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  {form.fields.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No fields to preview</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Switch to Build mode to add fields to your form.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {form.fields.map((field) => (
                        <div key={field.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderFieldPreview(field)}
                          {field.helpText && (
                            <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                          )}
                        </div>
                      ))}
                      
                      <div className="pt-6 border-t">
                        <button
                          disabled
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 opacity-50"
                        >
                          Submit Form
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </>
  );
};

export default FormBuilder;
